import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { 
  ArrowLeft, Play, Pause, Volume2, VolumeX, Maximize, 
  SkipBack, SkipForward, Users, Sparkles, Clock
} from "lucide-react";
import { useArenaRecordings, ArenaRecording } from "@/hooks/useArenaRecordings";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

const ArenaRecordingsPlayback: React.FC = () => {
  const { recordingId } = useParams<{ recordingId: string }>();
  const navigate = useNavigate();
  const { recordings, loading } = useArenaRecordings();
  const videoRef = useRef<HTMLVideoElement>(null);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [selectedHighlight, setSelectedHighlight] = useState<number | null>(null);

  const recording = recordings.find(r => r.id === recordingId);

  // Mock highlights for the timeline
  const highlights = [
    { time: 180, label: "First Pop! 🎉", type: "pop" },
    { time: 420, label: "Active discussion started", type: "activity" },
    { time: 780, label: "Match made! 💕", type: "match" },
    { time: 1200, label: "Peak engagement", type: "peak" },
    { time: 1800, label: "Group moment", type: "group" },
  ].filter(h => recording && h.time <= (recording.duration_seconds || 0));

  // Mock participants
  const mockParticipants = [
    { id: "1", name: "Sarah", photo_url: "/placeholder.svg", spoke_at: [60, 300, 600] },
    { id: "2", name: "Michael", photo_url: "/placeholder.svg", spoke_at: [120, 480, 900] },
    { id: "3", name: "Emma", photo_url: "/placeholder.svg", spoke_at: [180, 540, 1080] },
    { id: "4", name: "Alex", photo_url: "/placeholder.svg", spoke_at: [240, 720] },
  ];

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleTimeUpdate = () => setCurrentTime(video.currentTime);
    const handleLoadedMetadata = () => setDuration(video.duration);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("loadedmetadata", handleLoadedMetadata);
    video.addEventListener("ended", handleEnded);

    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Auto-hide controls
  useEffect(() => {
    if (!isPlaying) return;
    
    const timer = setTimeout(() => setShowControls(false), 3000);
    return () => clearTimeout(timer);
  }, [isPlaying, showControls]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value: number[]) => {
    const video = videoRef.current;
    if (!video || !recording) return;

    const seekTime = (value[0] / 100) * (recording.duration_seconds || duration);
    video.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const seekToHighlight = (time: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = time;
    setCurrentTime(time);
    setSelectedHighlight(time);
  };

  const skip = (seconds: number) => {
    const video = videoRef.current;
    if (!video) return;

    video.currentTime = Math.max(0, Math.min(video.duration, video.currentTime + seconds));
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (value: number[]) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = value[0] / 100;
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;

    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!recording) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Recording not found</p>
        <Button onClick={() => navigate("/live-arenas")}>Back to Arenas</Button>
      </div>
    );
  }

  const totalDuration = recording.duration_seconds || 0;
  const progress = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-20 p-4 bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/live-arenas")}
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-lg">{recording.arena_name}</h1>
            <p className="text-sm text-white/70">
              {format(new Date(recording.recorded_at), "MMM d, yyyy 'at' h:mm a")}
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm text-white/70">
            <span className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              {recording.participant_count}
            </span>
            <span className="flex items-center gap-1">
              <Sparkles className="w-4 h-4 text-pop" />
              {recording.pop_count} Pops
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatTime(totalDuration)}
            </span>
          </div>
        </div>
      </div>

      {/* Video Player */}
      <div 
        className="relative w-full h-[60vh] bg-black flex items-center justify-center cursor-pointer"
        onClick={() => setShowControls(true)}
        onMouseMove={() => setShowControls(true)}
      >
        {recording.video_url ? (
          <video
            ref={videoRef}
            src={recording.video_url}
            className="w-full h-full object-contain"
            playsInline
          />
        ) : (
          <div className="flex flex-col items-center gap-4 text-white/50">
            <div className="w-24 h-24 rounded-full bg-white/10 flex items-center justify-center">
              <Play className="w-10 h-10" />
            </div>
            <p>Video preview not available</p>
            <p className="text-sm">Recording data saved for {formatTime(totalDuration)}</p>
          </div>
        )}

        {/* Play/Pause overlay */}
        <div 
          className={cn(
            "absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity",
            showControls ? "opacity-100" : "opacity-0"
          )}
        >
          <Button
            variant="ghost"
            size="lg"
            onClick={(e) => { e.stopPropagation(); togglePlay(); }}
            className="w-20 h-20 rounded-full bg-white/20 hover:bg-white/30 text-white"
          >
            {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
          </Button>
        </div>

        {/* Bottom controls */}
        <div 
          className={cn(
            "absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent transition-opacity",
            showControls ? "opacity-100" : "opacity-0"
          )}
        >
          {/* Timeline with highlights */}
          <div className="mb-4 relative">
            <Slider
              value={[progress]}
              onValueChange={handleSeek}
              max={100}
              step={0.1}
              className="cursor-pointer"
            />
            
            {/* Highlight markers */}
            {highlights.map((highlight, index) => (
              <button
                key={index}
                onClick={() => seekToHighlight(highlight.time)}
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full transition-transform hover:scale-150",
                  highlight.type === "pop" && "bg-pop",
                  highlight.type === "match" && "bg-pink-500",
                  highlight.type === "activity" && "bg-blue-500",
                  highlight.type === "peak" && "bg-amber-500",
                  highlight.type === "group" && "bg-green-500",
                  selectedHighlight === highlight.time && "ring-2 ring-white"
                )}
                style={{ left: `${(highlight.time / totalDuration) * 100}%` }}
                title={highlight.label}
              />
            ))}
          </div>

          {/* Control buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => skip(-10)}
                className="text-white hover:bg-white/10"
              >
                <SkipBack className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={togglePlay}
                className="text-white hover:bg-white/10"
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => skip(10)}
                className="text-white hover:bg-white/10"
              >
                <SkipForward className="w-5 h-5" />
              </Button>
              <span className="text-sm ml-2">
                {formatTime(currentTime)} / {formatTime(totalDuration)}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleMute}
                className="text-white hover:bg-white/10"
              >
                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
              </Button>
              <div className="w-24">
                <Slider
                  value={[isMuted ? 0 : volume * 100]}
                  onValueChange={handleVolumeChange}
                  max={100}
                  step={1}
                />
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleFullscreen}
                className="text-white hover:bg-white/10"
              >
                <Maximize className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Highlights & Participants */}
      <div className="p-4 bg-background text-foreground">
        {/* Highlights Section */}
        <div className="mb-6">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pop" />
            Highlights
          </h3>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {highlights.length > 0 ? highlights.map((highlight, index) => (
              <button
                key={index}
                onClick={() => seekToHighlight(highlight.time)}
                className={cn(
                  "flex-shrink-0 px-4 py-2 rounded-full border transition-colors",
                  selectedHighlight === highlight.time
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-muted border-border hover:border-primary"
                )}
              >
                <span className="text-sm font-medium">{formatTime(highlight.time)}</span>
                <span className="text-xs ml-2 text-muted-foreground">{highlight.label}</span>
              </button>
            )) : (
              <p className="text-muted-foreground text-sm">No highlights in this recording</p>
            )}
          </div>
        </div>

        {/* Participants Section */}
        <div>
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Users className="w-5 h-5" />
            Participants ({recording.participant_count})
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {mockParticipants.slice(0, recording.participant_count || 4).map((participant) => (
              <div
                key={participant.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors cursor-pointer"
                onClick={() => {
                  if (participant.spoke_at[0]) {
                    seekToHighlight(participant.spoke_at[0]);
                  }
                }}
              >
                <img
                  src={participant.photo_url}
                  alt={participant.name}
                  className="w-10 h-10 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{participant.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Spoke {participant.spoke_at.length}x
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Other Recordings */}
        <div className="mt-8">
          <h3 className="font-semibold mb-3">More Recordings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {recordings.filter(r => r.id !== recordingId).slice(0, 4).map((rec) => (
              <button
                key={rec.id}
                onClick={() => navigate(`/arena-recordings/${rec.id}`)}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted hover:bg-muted/80 transition-colors text-left"
              >
                <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
                  <Play className="w-6 h-6" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{rec.arena_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatTime(rec.duration_seconds || 0)} • {rec.participant_count} participants
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArenaRecordingsPlayback;
